import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNotifications } from "../../context/Notifications";
import { parseEmailHtml } from "../../utils/parse/parse";
import { sendSMSService } from "../../services/sms/services";
import { useTranslator } from "../../context/translator";
import ModalContainer from "../modalContainer/ModalContainer";
import PhoneStepSelector from "../phoneStepSelector/PhoneStepSelector";
import SmsForm from "../smsForm/SmsForm";
import WhatsappForm from "../whatsappForm/WhatsappForm";
import {
  useGetCompanyQuery,
  useGetTemplatesQuery,
  useGetUserByIdQuery,
} from "store/api/apiSlice";

const PhoneNumberCommunicationModal = ({ customer, disabled = false }) => {
  const { user } = useSelector((state) => state.user);
  const { translate } = useTranslator();
  const [companyAllow, setCompanyAllow] = useState([]);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { data: templates, isLoading: templatesLoading } = useGetTemplatesQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  //const { smsTemplates = [], whatsappTemplates = [] } = useSelector((state) => state.template,);

  const [smsTemplates, setSmsTemplates] = useState([]);
  const whatsappTemplates = [];
  const { addNotification } = useNotifications();

  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState("select");
  const [isLoading, setIsLoading] = useState(false);
  const [template, setTemplate] = useState(smsTemplates?.[0]);
  const [whatsAppTemplate, setWhatsAppTemplate] = useState(
    whatsappTemplates?.[0],
  );

  const toggleModal = () => {
    setStep("select");
    setTemplate(smsTemplates?.[0]);
    setWhatsAppTemplate(whatsappTemplates?.[0]);
    setModalOpen((p) => !p);
  };

  const handleSendSMS = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const response = await sendSMSService({
      userId: userProfile?.user?._id,
      customerId: customer?._id,
      templateId: template?._id,
    });
    addNotification({
      id: Date.now(),
      message: response?.success
        ? translate("message-sent")
        : translate("error-sending-message"),
      kind: response?.success ? "success" : "danger",
    });
    setIsLoading(false);
    if (response?.success) toggleModal();
  };

  const handleSendWhatsapp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const response = { success: true };
    addNotification({
      id: Date.now(),
      message: response?.success
        ? translate("whatsapp-message-sent")
        : translate("error-sending-whatsapp-message"),
      kind: response?.success ? "success" : "danger",
    });
    setIsLoading(false);
    if (response?.success) toggleModal();
  };

  const smsPreview = parseEmailHtml(customer, user, company, template?.text);
  const whatsappPreview = parseEmailHtml(
    customer,
    user,
    company,
    whatsAppTemplate?.text,
  );

  useEffect(() => {
    if (templates && templates?.templates?.length > 0) {
      setSmsTemplates(
        templates?.templates?.filter(
          (t) => t.type === "sms" && !t.resetPassword && !t.newUser,
        ),
      );
    }
  }, [templates]);

  useEffect(() => {
    if (company?.allow) {
      setCompanyAllow({ ...company.allow });
    }
  }, [company]);

  if (userLoading || companyLoading || templatesLoading) return null;

  return (
    <>
      {customer?.phoneNumber
        ?.toString()
        ?.split(/[/]/)
        ?.map((num, i) => (
          <button
            key={`${customer?._id}-${i}`}
            onClick={toggleModal}
            disabled={disabled}
            className={`flex items-center text-sm font-medium me-3 ${
              disabled
                ? "text-gray-400 cursor-not-allowed dark:text-gray-500"
                : "text-blue-600 hover:underline dark:text-blue-400"
            }`}
          >
            <span
              className={`flex w-2.5 h-2.5 rounded-full me-1.5 ${
                disabled ? "bg-slate-300" : "bg-blue-600"
              }`}
            ></span>
            {num}
          </button>
        ))}

      {modalOpen && (
        <ModalContainer
          title={`${translate("phone-number-of")} ${customer?.fullName}`}
          onClose={toggleModal}
        >
          {step === "select" && (
            <PhoneStepSelector
              allow={companyAllow}
              customer={customer}
              onSelect={setStep}
              user={user}
            />
          )}

          {step === "sms" && (
            <SmsForm
              smsTemplates={smsTemplates}
              template={template}
              onTemplateChange={(e) =>
                setTemplate(smsTemplates.find((t) => t._id === e.target.value))
              }
              onSubmit={handleSendSMS}
              isLoading={isLoading}
              translate={translate}
              previewHtml={smsPreview}
            />
          )}

          {step === "whatsapp" && (
            <WhatsappForm
              whatsappTemplates={whatsappTemplates}
              selectedTemplate={whatsAppTemplate}
              onChange={(e) =>
                setWhatsAppTemplate(
                  whatsappTemplates.find((t) => t._id === e.target.value),
                )
              }
              onSubmit={handleSendWhatsapp}
              isLoading={isLoading}
              translate={translate}
              previewHtml={whatsappPreview}
            />
          )}
        </ModalContainer>
      )}
    </>
  );
};

export default PhoneNumberCommunicationModal;
